/**
 * Discreet in-app banner shown on the Dashboard when the user
 * has not yet subscribed to push notifications.
 * Dismissible (session-based) + triggers OneSignal slidedown on click.
 */
import { useEffect, useState } from 'react';
import { BellRinging, X } from '@phosphor-icons/react';
import {
  promptForPushPermission,
  getPermissionState,
  isPushSubscribed,
  initOneSignal,
} from '../lib/onesignal';

const DISMISSED_KEY = 'push_banner_dismissed_v1';

export default function PushOptInBanner() {
  const [visible, setVisible] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      if (sessionStorage.getItem(DISMISSED_KEY) === '1') return;

      await initOneSignal();
      // small wait for SDK state to settle
      await new Promise((r) => setTimeout(r, 600));

      if (!mounted) return;

      const perm = getPermissionState();
      if (perm === 'unsupported' || perm === 'denied' || perm === 'granted') {
        // Already decided or unavailable: don't show
        return;
      }
      // Only show if not already subscribed
      if (isPushSubscribed()) return;

      setVisible(true);
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const handleEnable = async () => {
    setBusy(true);
    await promptForPushPermission();
    setBusy(false);
    // Hide immediately; if they accept, we won't show again next session anyway
    sessionStorage.setItem(DISMISSED_KEY, '1');
    setVisible(false);
  };

  const handleDismiss = () => {
    sessionStorage.setItem(DISMISSED_KEY, '1');
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      className="relative bg-gradient-to-r from-indigo-600 via-violet-600 to-purple-600 text-white px-4 sm:px-6 py-3 flex items-center gap-3 sm:gap-4 shadow-lg"
      data-testid="push-optin-banner"
    >
      <div className="w-9 h-9 flex-shrink-0 bg-white/20 border border-white/30 backdrop-blur flex items-center justify-center">
        <BellRinging size={18} weight="fill" className="text-white animate-pulse" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-black leading-tight">
          Attiva le notifiche push
        </p>
        <p className="text-[11px] text-white/85 leading-snug hidden sm:block">
          Aggiornamenti sui tuoi contratti, schedine e eventi · zero spam
        </p>
      </div>
      <button
        onClick={handleEnable}
        disabled={busy}
        className="bg-white text-indigo-700 hover:bg-indigo-50 font-bold text-[11px] uppercase tracking-wider px-3 sm:px-4 py-2 transition-colors disabled:opacity-60"
        data-testid="push-optin-enable-btn"
      >
        {busy ? '...' : 'Attiva'}
      </button>
      <button
        onClick={handleDismiss}
        className="w-8 h-8 flex items-center justify-center text-white/70 hover:text-white hover:bg-white/10 transition-colors flex-shrink-0"
        aria-label="Chiudi"
        data-testid="push-optin-dismiss-btn"
      >
        <X size={16} weight="bold" />
      </button>
    </div>
  );
}
